import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";

// Third-party packages.
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Constants.
import { DATA_LOADED, DATA_LOADING } from "../../../component/Constants";

// Components.
import ReactTable from "../../../component/ReactTable";
import {
    fetchResults,
    getDefaultPageSize,
    stringFormatting,
} from "../../../helpers/utils";
import StatisticApi from '../../../api/instance/StatisticApi';
import InstanceApi from '../../../api/instance/InstanceApi';
import { showError } from '../../../utils/showAlert';
import ExtendedStatistics from "../../../types/ExtendedStatistics";
import SearchInput from '../../../component/SearchInput';

const List = () => {
    const [statisticOptionsList, setStatisticOptionsList] = useState<any[]>([]);
    const [displayMap, setDisplayMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const getTotalSelected = (list: any) => {
        let count = 0;
        for (let i = 0; i < list.length; i++) {
            if (list[i].collect === true) {
                count++;
            }
        }
        return count;
    }

    const getCollectStatisticsList = async() => {
        try {
            const results: any[][] = await fetchResults(
                [StatisticApi.fetchTypes(), InstanceApi.fetchTypes()]);
            const map: Record<string, string> = {};
            results[1].forEach((t: { type: string, display: string }) => {
                map[t.type] = t.display;
            });
            setDisplayMap(map);
            setLoading(DATA_LOADED);
            return createStatisticTableData(results[0])
        } catch (error: any) {
            console.error(error);
            const errorMessage = error.message || error.toString();
            showError('Failed to load DB statistics types', errorMessage);
            setLoading(DATA_LOADED);
            return [];
        }
    }

    useEffect(() => {
        // @ts-ignore
        getCollectStatisticsList().then(setStatisticOptionsList)
    }, []);

    const createStatisticTableData = (list: ExtendedStatistics[]) => {
        const arr: any[] = []
        list.forEach((item: ExtendedStatistics) => {
            const dbExist = arr.find(db => db?.name === item.type)
            if (!dbExist) {
                arr.push({
                    name: item.type,
                    list: [item]
                })
            } else {
                dbExist.list.push(item)
            }
        })
        // Precompute numeric totals so the metric columns can sort.
        arr.forEach(db => {
            db.total = db.list.length;
            db.selected = getTotalSelected(db.list);
        })
        return arr
    }

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data: ExtendedStatistics[] = useMemo(() => statisticOptionsList.filter(option => {
        const search = tableFilter.toLowerCase();
        const name = (option.name === null ? '' : option.name).toLowerCase();
        const display = (displayMap[option.name] || '').toLowerCase();
        return name.includes(search) || display.includes(search);
    }), [statisticOptionsList, tableFilter, displayMap]);


    const columns: Column[] = useMemo(() => [
        {
            Header: 'Database',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            Cell: ({cell}) => (
                <React.Fragment>
                    {displayMap[cell.row.values.name] || stringFormatting(cell.row.values.name)}
                </React.Fragment>
            )
        },
        {
            Header: 'Total Metrics',
            accessor: 'total',
            className: 'fw-bold text-center',
            headerClassName: 'text-center',
            Cell: ({cell}) => (
                <React.Fragment>
                    {cell.row.values.total ?? 0}
                </React.Fragment>
            )
        },
        {
            Header: 'Selected Metrics',
            accessor: 'selected',
            className: 'fw-bold text-center',
            headerClassName: 'text-center',
            Cell: ({cell}) => (
                <React.Fragment>
                    {cell.row.values.selected ?? 0}
                </React.Fragment>
            )
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({cell}) => (
                <React.Fragment>
                    <Link to={`/admin/database-statistics/edit/${cell.row.values.name}`} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                </React.Fragment>
            )
        }
    ], [data, displayMap]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{id: 'name', desc: false}]
            }
        },
        useSortBy, usePagination);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                    Statistics
                    <span className="badge bg-info" data-tooltip-id="global-tooltip" data-tooltip-content="Total Statistics">{data.length}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseStatistics" aria-expanded="false" aria-controls="collapseStatistics"/>
                </div>
                <div id="collapseStatistics" className="card-body collapse show">

                    <React.Fragment>
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <ReactTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            dataLength={data.length}
                            error={null}
                            loading={loading === DATA_LOADING}
                        />
                    </React.Fragment>
                </div>
            </div>
        </div>
    )
}

export default List;
